// Measure the actual print layout, including the repeated header and last-page
// totals. A row count alone cannot account for fonts, wrapping or page margins.
export const paginatePrintPages = async (container, {
    pageHeightMm,
    verticalInset = 0,
    tableSelector = '.content-table',
    repeatNotes = false,
    showReporter = true,
}) => {
    const doc = container.ownerDocument || container;
    await doc.fonts?.ready;

    const source = container.querySelector('.page');
    const table = source.querySelector(tableSelector);
    const rows = Array.from(table.tBodies[0].rows);
    const tableFooter = table.tFoot?.cloneNode(true);
    const summary = [];
    for (let node = table.nextElementSibling; node; node = node.nextElementSibling) {
        summary.push(node.cloneNode(true));
    }

    const template = source.cloneNode(true);
    const templateTable = template.querySelector(tableSelector);
    templateTable.tBodies[0].replaceChildren();
    templateTable.tFoot?.remove();
    // Bon de Sortie keeps its note on every page, with the total only at the end.
    if (!repeatNotes) {
        while (templateTable.nextSibling) templateTable.nextSibling.remove();
    }

    const pages = [];
    let start = 0;
    do {
        const pageStart = start;
        const page = template.cloneNode(true);
        source.before(page);
        const pageTable = page.querySelector(tableSelector);
        const tbody = pageTable.tBodies[0];
        const pageStyle = doc.defaultView.getComputedStyle(page);
        const availableHeight = pageHeightMm * 96 / 25.4 - verticalInset
            - (parseFloat(pageStyle.paddingBottom) || 0);
        const fits = () => {
            const last = page.lastElementChild;
            const bottomMargin = parseFloat(doc.defaultView.getComputedStyle(last).marginBottom) || 0;
            // Leave one pixel for browser print rounding.
            return last.getBoundingClientRect().bottom + bottomMargin
                <= page.getBoundingClientRect().top + availableHeight - 1;
        };
        const setRows = (count) => tbody.replaceChildren(
            ...rows.slice(pageStart, pageStart + count).map(row => row.cloneNode(true))
        );

        // First try to finish the document on this page, with all totals/notes.
        const ending = repeatNotes ? [] : summary.map(node => node.cloneNode(true));
        page.append(...ending);
        if (tableFooter) pageTable.append(tableFooter.cloneNode(true));
        const remaining = rows.length - start;
        setRows(remaining);
        if (fits()) {
            pages.push(page);
            break;
        }

        ending.forEach(node => node.remove());
        pageTable.tFoot?.remove();
        if (showReporter) {
            const reporter = doc.createElement('div');
            reporter.style.cssText = 'text-align: right; margin-top: 5px; font-style: italic; font-size: 11px;';
            reporter.textContent = 'À reporter';
            page.append(reporter);
        }

        // Fill continuation pages, leaving at least one item with the totals.
        let low = 0;
        let high = remaining - 1;
        while (low < high) {
            const count = Math.ceil((low + high) / 2);
            setRows(count);
            if (fits()) low = count;
            else high = count - 1;
        }
        if (low === 0) {
            throw new Error('Une ligne ou le récapitulatif dépasse la hauteur imprimable de la page.');
        }
        setRows(low);
        pages.push(page);
        start += low;
    } while (start < rows.length);

    source.remove();
    pages.forEach((page, index) => {
        page.querySelector('[data-page-number]').textContent = `${index + 1} / ${pages.length}`;
    });
    return pages;
};

export const paginateDirectPrint = async (doc, pageHeightMm, options = {}) => {
    // Match the existing @page printable area and 30 mm header padding while
    // measuring on screen; @media print is not active until print() is called.
    doc.querySelector('.page').style.cssText = 'padding-top: 30mm; box-sizing: border-box;';
    const bodyStyle = doc.defaultView.getComputedStyle(doc.body);
    const verticalInset = ['marginTop', 'marginBottom', 'paddingTop', 'paddingBottom']
        .reduce((sum, property) => sum + (parseFloat(bodyStyle[property]) || 0), 0);
    return paginatePrintPages(doc, { ...options, pageHeightMm, verticalInset });
};
